import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, FileText, User, Mail, Clock, Shield, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Verify() {
  const { documentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadVerification = async () => {
      try {
        const response = await axios.get(`${API}/document/${documentId}/verify`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Dokument nicht gefunden' : 'Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    };
    loadVerification();
  }, [documentId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-zinc-300 border-t-zinc-900 rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Verifikation fehlgeschlagen</h1>
          <p className="text-zinc-600">{error}</p>
        </Card>
      </div>
    );
  }

  if (!data?.verification_enabled) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center">
          <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Verifikation nicht aktiviert</h1>
          <p className="text-zinc-600">Für dieses Dokument ist die Online-Verifikation nicht aktiviert.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center h-20 w-20 rounded-full mb-4 ${
            data.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'
          }`}>
            {data.status === 'completed' ? (
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            ) : (
              <Clock className="h-10 w-10 text-amber-600" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-zinc-900 mb-2">
            {data.status === 'completed' ? 'Dokument verifiziert' : 'Dokument ausstehend'}
          </h1>
          <p className="text-zinc-600">
            {data.status === 'completed' 
              ? 'Dieses Dokument wurde erfolgreich signiert und ist gültig.'
              : 'Dieses Dokument wartet noch auf Unterschriften.'}
          </p>
        </div>

        {/* Document Info */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="h-6 w-6 text-zinc-600" />
            <h2 className="text-lg font-semibold">Dokumentinformationen</h2>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-600">Dokument-ID:</span>
              <span className="font-mono text-xs">{data.document_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-600">Dateiname:</span>
              <span className="font-medium">{data.original_filename}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-600">Erstellt:</span>
              <span>{formatDate(data.created_at)}</span>
            </div>
            {data.completed_at && (
              <div className="flex justify-between">
                <span className="text-zinc-600">Abgeschlossen:</span>
                <span>{formatDate(data.completed_at)}</span>
              </div>
            )}
            {data.is_beschlussprotokoll && (
              <div className="flex justify-between">
                <span className="text-zinc-600">Typ:</span>
                <span className="text-blue-600 font-medium">Beschlussprotokoll</span>
              </div>
            )}
          </div>
        </Card>

        {/* Signatures */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-zinc-600" />
            <h2 className="text-lg font-semibold">Unterschriften ({data.signatures.length})</h2>
          </div>
          
          {data.signatures.length === 0 ? (
            <p className="text-zinc-500 text-center py-4">Noch keine Unterschriften vorhanden</p>
          ) : (
            <div className="space-y-4">
              {data.signatures.map((sig, index) => (
                <div key={index} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-green-800">{sig.signer_name || 'Unbekannt'}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-zinc-600">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span>{sig.signer_email || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{formatDate(sig.signed_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-zinc-500">
          <p>Verifiziert durch SignFlow</p>
          <p className="mt-1">Scholze-Kurz & Kurz Immobilien GmbH</p>
        </div>
      </div>
    </div>
  );
}
